import {
    ColumnSuffix, ExtractedFieldKey, FieldMap, getField, numericValue,
} from './cells';

/**
 * The arithmetic the Executive Summary applies to the four extracted primitives per column -
 * effective gross revenue, operating expenses, capital expenditures and cap rate. The schema
 * stores a `_trended` / `_at_sale` twin for those four only, so every other figure in the
 * Operations and Development returns blocks is computed here for the columns that have no key
 * (`pro_forma_executive_summary_schema.py`, and `utils/pro_forma/common/disposition.py`).
 */

/** `net_operating_income = effective_gross_revenue - operating_expenses` */
export const netOperatingIncome = (fieldMap: FieldMap, suffix: ColumnSuffix): number | undefined => {
    const revenue = numericValue(getField(fieldMap, `operations.effective_gross_revenue${suffix}`));
    const expenses = numericValue(getField(fieldMap, `operations.operating_expenses${suffix}`));
    return revenue !== undefined && expenses !== undefined ? revenue - expenses : undefined;
};

/** `cash_flow_from_operations = net_operating_income - capital_expenditures` */
export const cashFlowFromOperations = (fieldMap: FieldMap, suffix: ColumnSuffix): number | undefined => {
    const income = netOperatingIncome(fieldMap, suffix);
    const capitalExpenditures = numericValue(getField(fieldMap, `operations.capital_expenditures${suffix}`));
    return income !== undefined && capitalExpenditures !== undefined ? income - capitalExpenditures : undefined;
};

/** `valuation = net_operating_income / cap_rate` */
export const valuation = (fieldMap: FieldMap, suffix: ColumnSuffix): number | undefined => {
    const income = netOperatingIncome(fieldMap, suffix);
    const capRate = numericValue(getField(fieldMap, `operations.cap_rate${suffix}`));
    return income !== undefined && capRate ? income / capRate : undefined;
};

/** `valuation_per_unit = valuation / details.units` */
export const valuationPerUnit = (fieldMap: FieldMap, suffix: ColumnSuffix): number | undefined => {
    const value = valuation(fieldMap, suffix);
    const units = numericValue(getField(fieldMap, 'details.units'));
    return value !== undefined && units ? value / units : undefined;
};

/** `pct_of_total_project_cost = valuation / development.total_uses` */
export const percentOfTotalProjectCost = (fieldMap: FieldMap, suffix: ColumnSuffix): number | undefined => {
    const value = valuation(fieldMap, suffix);
    const totalUses = numericValue(getField(fieldMap, 'development.total_uses'));
    return value !== undefined && totalUses ? value / totalUses : undefined;
};

/** `yield_on_cost = net_operating_income / development.total_uses` */
export const yieldOnCost = (fieldMap: FieldMap, suffix: ColumnSuffix): number | undefined => {
    const income = netOperatingIncome(fieldMap, suffix);
    const totalUses = numericValue(getField(fieldMap, 'development.total_uses'));
    return income !== undefined && totalUses ? income / totalUses : undefined;
};

/** `development_spread (bps) = (yield_on_cost - cap_rate) * 10000` */
export const developmentSpreadBps = (fieldMap: FieldMap, suffix: ColumnSuffix): number | undefined => {
    const yieldValue = yieldOnCost(fieldMap, suffix);
    const capRate = numericValue(getField(fieldMap, `operations.cap_rate${suffix}`));
    return yieldValue !== undefined && capRate !== undefined ? (yieldValue - capRate) * 10000 : undefined;
};

/**
 * `percent = amount / total`, for a Development uses row (of `development.total_uses`) or a
 * sources row (of `development.total_sources`) - the schema stores the amount only.
 */
export const developmentPercentOfTotal = (
    fieldMap: FieldMap, amountKey: ExtractedFieldKey, totalKey: ExtractedFieldKey,
): number | undefined => {
    const amount = numericValue(getField(fieldMap, amountKey));
    const total = numericValue(getField(fieldMap, totalKey));
    return amount !== undefined && total ? amount / total : undefined;
};

/** `$ / Built SF = amount / details.gross_built_sf`, for a Development uses/sources row. */
export const developmentPerBuiltSf = (fieldMap: FieldMap, amountKey: ExtractedFieldKey): number | undefined => {
    const amount = numericValue(getField(fieldMap, amountKey));
    const grossBuiltSf = numericValue(getField(fieldMap, 'details.gross_built_sf'));
    return amount !== undefined && grossBuiltSf ? amount / grossBuiltSf : undefined;
};

/** `$ / Unit = amount / details.units`, for a Development uses/sources row. */
export const developmentPerUnit = (fieldMap: FieldMap, amountKey: ExtractedFieldKey): number | undefined => {
    const amount = numericValue(getField(fieldMap, amountKey));
    const units = numericValue(getField(fieldMap, 'details.units'));
    return amount !== undefined && units ? amount / units : undefined;
};
